/* Elva Email — mock data (from reference) */
import type { EmTagKey, EmThread } from './types';
import { emailBodyToPlainText } from './formatEmailBody';

export const EM_CLINIC = "info@unionsquare-dental.com";

export const EM_TONE = {
  "success": {
    "fg": "var(--success)",
    "bg": "var(--success-tint)"
  },
  "gold": {
    "fg": "var(--gold)",
    "bg": "var(--gold-tint)"
  },
  "warn": {
    "fg": "var(--terracotta)",
    "bg": "var(--terracotta-tint)"
  },
  "ai": {
    "fg": "var(--ai-3)",
    "bg": "var(--ai-tint)"
  },
  "info": {
    "fg": "var(--info)",
    "bg": "var(--info-tint)"
  },
  "neutral": {
    "fg": "var(--ink-2)",
    "bg": "var(--cream-0)"
  }
} as const;

export const EM_TAGS = {
  emergency: { label: 'Emergency', tone: 'warn', icon: 'alert' },
  complaint: { label: 'Complaint', tone: 'warn', icon: 'flame' },
  booking: { label: 'Booking', tone: 'success', icon: 'calendar' },
  insurance: { label: 'Insurance', tone: 'gold', icon: 'shield' },
  billing: { label: 'Billing', tone: 'gold', icon: 'dollar' },
  new_inquiry: { label: 'New inquiry', tone: 'ai', icon: 'sparkle' },
  referral: { label: 'Referral', tone: 'info', icon: 'link' },
  personal: { label: 'Personal', tone: 'info', icon: 'heart' },
  marketing: { label: 'Sales / Marketing', tone: 'neutral', icon: 'globe' },
  spam: { label: 'Spam', tone: 'neutral', icon: 'shield-off' },
} as const;

export const EM_FOLDERS = [
  { id: 'billing', label: 'Billing', icon: 'dollar' },
  { id: 'referrals', label: 'Referrals', icon: 'link' },
  { id: 'drmitchell', label: 'Dr. Mitchell', icon: 'team' },
  { id: 'drokafor', label: 'Dr. Okafor', icon: 'team' },
  { id: 'insurance', label: 'Insurance claims', icon: 'shield' },
  { id: 'vendors', label: 'Vendors', icon: 'globe' },
] as const;

/**
 * Fixed email topic vocabulary (spec D7). One classification axis — folders retired.
 * Shape mirrors SMS MS_TOPICS: { label, color }. AI Recep assigns one current topic.
 */
export const EM_TOPICS = {
  emergency:         { label: 'Emergency',         color: 'var(--terracotta)' },
  booking:           { label: 'Booking',           color: 'var(--success)' },
  billing:           { label: 'Billing',           color: 'var(--gold)' },
  insurance:         { label: 'Insurance',         color: 'var(--gold)' },
  new_patient:       { label: 'New patient',       color: 'var(--ai-3)' },
  clinical_question: { label: 'Clinical question', color: 'var(--info)' },
  records_forms:     { label: 'Records & forms',   color: 'var(--info)' },
  complaint:         { label: 'Complaint',         color: 'var(--terracotta)' },
  vendor:            { label: 'Vendor',            color: 'var(--ink-3)' },
  referral:          { label: 'Referral',          color: 'var(--info)' },
  marketing:         { label: 'Sales / Marketing', color: 'var(--ink-3)' },
  spam:              { label: 'Spam',              color: 'var(--ink-3)' },
} as const;

export const EM_HEALTH = {
  drafted: { tone: 'gold', label: 'Draft held' },
  drafted_low_conf: { tone: 'gold', label: 'Draft held' },
  held: { tone: 'gold', label: 'Draft held' },
  sent: { tone: 'success', label: 'Sent' },
  replied: { tone: 'success', label: 'Replied' },
  needs_reply: { tone: 'warn', label: 'Needs reply' },
} as const;

export const EM_REPLY_STATE_LABELS = {
  needs_reply: { tone: 'warn', label: 'Needs reply' },
  drafted: { tone: 'gold', label: 'Drafted' },
  responded: { tone: 'success', label: 'Responded' },
  no_reply_needed: { tone: 'neutral', label: 'No reply needed' },
} as const;

export const EM_CONTACTS: { id: string; name: string; email: string; initials: string }[] = [];

export const EM_THREADS: EmThread[] = [];

export const EM_SUM: Record<string, string> = {};

export function emInitials(n: string): string {
  return n
    .split(' ')
    .filter((w) => w.length > 1)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export function emSummary(
  threadId: string,
  m: { id: string; body?: string; bodyTr?: string },
): string {
  const key = threadId + ':' + m.id;
  const map = EM_SUM as Record<string, string>;
  if (map[key]) return map[key];
  const source = m.bodyTr || m.body || '';
  let lines = source
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  const first = lines[0];
  if (first && /^(hi|hello|hey|dear|hola)\b/i.test(first) && first.length < 42) lines.shift();
  const body = emailBodyToPlainText(lines.join('\n'));
  return body.length > 150 ? body.slice(0, 150).replace(/\s+\S*$/, '') + '…' : body;
}

export function emThreadInFolder(t: EmThread, fid: string): boolean {
  switch (fid) {
    case 'billing':
      return t.tags.includes('billing');
    case 'referrals':
      return t.tags.includes('referral');
    case 'insurance':
      return t.tags.includes('insurance');
    case 'vendors':
      return t.tags.includes('marketing') || t.patient.status === 'Vendor';
    case 'drmitchell':
      return !!(t.patient.next && /Mitchell/i.test(t.patient.next.prov || ''));
    case 'drokafor':
      return !!(t.patient.next && /Okafor/i.test(t.patient.next.prov || ''));
    default:
      return false;
  }
}
